"use client";

import { useTransition } from "react";
import { useRouter } from "next/navigation";
import useReportCaughtError from "@components/ErrorBoundary/useReportCaughtError";
import { FALLBACK_HEADING, FALLBACK_MESSAGE, FALLBACK_TRY_AGAIN } from "@components/ErrorBoundary/copy";

// Root boundary: the only one that catches [siteId]/layout.tsx's own SSR render
// throwing — most importantly its server-side `config` query during a Bridge or
// config outage (a segment's error.tsx renders INSIDE that segment's layout, so
// [siteId]/error.tsx can never see its layout throw). It renders inside the root
// layout but OUTSIDE [siteId]'s <Providers>, so it has no Apollo client to fetch
// tokens with and no themed CSS vars. It therefore depends on nothing: copy is
// the sanctioned hardcoded fallback and all styling is inline so it renders even
// if a CSS chunk never loaded — the same degraded contract as the provider-less
// AppsignalProvider fallback.
export default function RootError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const router = useRouter();
  useReportCaughtError(error);

  // The crash this catches is usually [siteId]/layout's Server Component config
  // query, so recovery must re-run the server render: a bare reset() replays the
  // cached RSC error without a refetch and leaves the user stuck even after the
  // outage clears. router.refresh() busts that cache; batched with reset() in a
  // transition, with isPending dimming/disabling the button so a slow refetch
  // doesn't look like a dead button. useRouter works here even though this
  // boundary renders outside <Providers> — it reads Next's own router context.
  const [isPending, startTransition] = useTransition();
  const tryAgain = () =>
    startTransition(() => {
      router.refresh();
      reset();
    });

  return (
    <div
      role="alert"
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "1rem",
        padding: "2rem",
        textAlign: "center",
        fontFamily:
          'system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
        color: "#1f2937",
        background: "#ffffff",
      }}
    >
      <h1 style={{ margin: 0, fontSize: "1.25rem", fontWeight: 600 }}>{FALLBACK_HEADING}</h1>
      <p style={{ margin: 0, maxWidth: "28rem", lineHeight: 1.5, color: "#4b5563" }}>
        {FALLBACK_MESSAGE}
      </p>
      <button
        type="button"
        onClick={tryAgain}
        disabled={isPending}
        style={{
          appearance: "none",
          border: "1px solid #d1d5db",
          borderRadius: "0.375rem",
          background: "#f9fafb",
          color: "#111827",
          padding: "0.5rem 1.25rem",
          fontSize: "0.9375rem",
          fontWeight: 500,
          cursor: isPending ? "wait" : "pointer",
          opacity: isPending ? 0.6 : 1,
        }}
      >
        {FALLBACK_TRY_AGAIN}
      </button>
    </div>
  );
}
